import { Box, Flex, Heading, Icon, Progress, Text } from '@everlywell/ui-kit';
import { Star } from '@phosphor-icons/react';
import React, { useId } from 'react';

export type ReviewCardProps = {
  total: number;
  rating: number;
  histogram: [number, number, number, number, number];
};

/**
 * Render the review card
 */
function ReviewsCard(props: ReviewCardProps): JSX.Element {
  const id = useId();

  // the histogram is an array of the number of reviews for each star rating
  // we need to reverse it so that the 5 star rating is at the top
  const reversedHistogram = props.histogram.slice().reverse();

  return (
    <Flex
      paddingX={[6, 60]}
      paddingY={[8, 60]}
      flexDirection="column"
      background="tints.lightCream"
      borderRadius={12}
      gap={4}
      // desktop width from the design
      width={['100%', '551px']}
    >
      <Flex gap={4} alignItems="center">
        <Icon
          as={Star}
          color={'utility.rating'}
          fontSize={'24px'}
          weight="fill"
        />
        <Heading size="md" color="tints.black" fontWeight="medium">
          <strong>{props.rating.toFixed(1)}</strong>{' '}
          <Text as="span" size="lg" color="tints.darkGrey">
            out of
          </Text>{' '}
          <strong>5</strong>
        </Heading>
      </Flex>
      <Flex as="ul" flexDirection="column" gap={2}>
        {reversedHistogram.map((value, index) => {
          const percentage = Math.round((value / props.total) * 100);
          const label = `${5 - index} star`;

          return (
            <ReviewBar
              key={`${id}-${index}`}
              label={label}
              color={
                index === 0 ? 'colors.utility.rating' : 'colors.tints.uiGrey'
              }
              percentage={percentage}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}

export default ReviewsCard;

export type ReviewBarProps = {
  label: string;
  color: string;
  percentage: number;
};

function ReviewBar(props: ReviewBarProps) {
  return (
    <Flex as="li" gap={2} alignItems="center">
      <Text size="md" color="tints.darkGrey" minWidth={34}>
        {props.label}
      </Text>
      <Box flex={1}>
        <Progress
          size="md"
          value={props.percentage}
          background="colors.tints.white"
          filledBackground={props.color}
          aria-label={`${props.percentage.toFixed(0)}% of the reviews are ${
            props.label
          }`}
        />
      </Box>

      <Text size="md" color="tints.darkGrey">
        {props.percentage.toFixed(0)}%
      </Text>
    </Flex>
  );
}
